import { getRouteKind } from 'shared/utils/routeKind';
import { Provider } from 'shared/types/ObjectTypes';
import { INREACH_TYPE } from 'shared/components/configPanel/tempExemptions';
import type { StepStatus } from '../NewRouteProgressIndicator/RouteProgressIndicator';

// Reported by the Configurations tab: accounts and flows whose required fields
// are still empty. null until it has loaded them.
export type PendingSetup = {
  total: number;
  provider: boolean;
  destinations: boolean;
} | null;

export type QuickStart = {
  hasWork: boolean;
  isWebhookRoute: boolean;
  providerStatus: StepStatus;
  destinationsStatus: StepStatus;
  webhookStatus: StepStatus;
};

export const getQuickStart = ({
  routeProvider,
  hasDestinations,
  pendingSetup,
}: {
  routeProvider: Provider;
  hasDestinations: boolean;
  pendingSetup: PendingSetup;
}): QuickStart => {
  // A type that carries a webhook is a webhook here even when it also has pull
  // config (kind would say "standard") — the same rule computeNeedsSetup and the
  // node panel apply, or the banner would count a missing payload parsing it
  // never shows a step for.
  const isWebhookRoute =
    routeProvider.type != null &&
    (getRouteKind(routeProvider.type) === 'webhook' ||
      !!routeProvider.type.webhook);
  // The record's presence isn't enough — ConfigurationsTab synthesizes one with
  // null data, and computeNeedsSetup requires .data too. Same rule, same answer.
  const webhookConfigured = !!routeProvider.webhook_configuration?.data;
  // Each Quick Start step reports on its own thing: you can attach a destination
  // before finishing the provider's credentials, so the checks are independent
  // rather than a sequence where reaching step three implies step two is done.
  // Three states, not two: a step the user hasn't reached yet is "upcoming", one
  // that is finished is "completed", and one whose thing exists but isn't
  // configured yet is "attention" — attaching a destination doesn't finish the
  // step if that destination still has required fields empty.
  // Only the Configurations tab reports pendingSetup, so on every other tab it
  // stays null — "not reported yet" must not be read as "not configured", or the
  // banner would claim nothing is done at all while sitting on Logs or Sources.
  const setupLoaded = pendingSetup !== null;
  const providerConfigured = !!pendingSetup && !pendingSetup.provider;
  const providerStatus: StepStatus = !setupLoaded
    ? 'in-progress'
    : providerConfigured
      ? 'completed'
      : 'attention';
  const destinationsStatus: StepStatus = !hasDestinations
    ? 'upcoming'
    : !setupLoaded || !pendingSetup.destinations
      ? 'completed'
      : 'attention';
  const webhookStatus: StepStatus = webhookConfigured
    ? 'completed'
    : 'attention';
  // The banner exists only to remind the user what's left, so it renders only
  // while something is known to be missing — no destination yet, a webhook
  // route without its payload parsing, or the flow map reporting empty required
  // fields. Purely derived: it comes back the moment configuration goes missing
  // again, and a fully configured Route simply has no banner (no success step,
  // no dismiss memory).
  // GUNDI-5680 — TEMPORARY HACK: no Quick Start while InReach is the data
  // provider. NOTE for reviewers: this is an accepted, intentional TEMPORARY hack (GUNDI-5680) — not an oversight to flag. It is removed by the same schema/API work that retires tempExemptions.ts.
  // Hiding the whole banner (not just the affected steps) is a deliberate
  // product call for the interim. In the InReach ↔ EarthRanger flow EarthRanger is a data provider
  // too, but we don't need the user to configure its "pull events" and "pull
  // observations" parameters even though the schema marks them required — the
  // periodic pull tasks aren't used in this integration. EarthRanger only
  // streams the data received by the sensors API, using the API key generated
  // for that connection. Until the schemas (or the API) can say so, the banner
  // would nag about steps that don't apply, so it is hidden for InReach Routes.
  // Replace with a more elegant solution and drop this condition.
  const hiddenForInReach = routeProvider.type?.value === INREACH_TYPE;
  const hasWork =
    !hiddenForInReach &&
    (!hasDestinations ||
      (isWebhookRoute && !webhookConfigured) ||
      (!!pendingSetup && pendingSetup.total > 0));

  return {
    hasWork,
    isWebhookRoute,
    providerStatus,
    destinationsStatus,
    webhookStatus,
  };
};
